import React, { useState } from 'react';
import { Plus, Trash2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { capUpdateProject } from '../../lib/capstoneApi';
import { inputCls, btnGhost } from './shared';

export default function TeamTab({ project, tasks, reloadProject }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'Member' });
  const canEdit = ['lead', 'admin'].includes(project.my_role);

  const save = async (members, mentors) => {
    try { await capUpdateProject(project.id, { members, mentors }); reloadProject(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update team'); }
  };

  const workload = (m) => tasks.filter((t) => t.owner && (t.owner === m.name || t.owner === m.email)).length;
  const workloadOpen = (m) => tasks.filter((t) => t.owner && (t.owner === m.name || t.owner === m.email) && t.status !== 'Done').length;

  return (
    <div className="max-w-3xl" data-testid="cap-team-tab">
      <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-amber-300 mb-4">Team members</div>
      <div className="space-y-2.5 mb-4">
        {(project.members || []).map((m, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-900/50 p-3.5" data-testid={`cap-team-member-${i}`}>
            <div className="w-9 h-9 rounded-full bg-amber-300/15 text-amber-300 flex items-center justify-center font-bold text-sm shrink-0">
              {(m.name || m.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] text-white/90 font-medium truncate">{m.name || m.email}</div>
              <div className="text-[11px] text-white/40 truncate">{m.email} · {m.role}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[13px] font-bold text-white/80">{workloadOpen(m)}<span className="text-white/30 font-normal">/{workload(m)}</span></div>
              <div className="text-[9.5px] text-white/35 uppercase tracking-wider">open/total tasks</div>
            </div>
            {canEdit && (m.role || '').toLowerCase() !== 'lead' && (
              <button onClick={() => save(project.members.filter((_, j) => j !== i), project.mentors)} className="text-white/25 hover:text-rose-300 ml-1"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
      {canEdit && !adding && <button className={btnGhost} onClick={() => setAdding(true)} data-testid="cap-team-add-btn"><Plus className="w-3.5 h-3.5" /> Add member</button>}
      {adding && (
        <div className="flex flex-wrap gap-2 items-center rounded-xl border border-white/12 p-3 bg-slate-900/60">
          <input className={`${inputCls} flex-1 min-w-[120px]`} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className={`${inputCls} flex-1 min-w-[170px]`} placeholder="email@university.edu" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select className={`${inputCls} w-28`} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option>Member</option><option>Lead</option>
          </select>
          <button className={btnGhost} disabled={!form.email.trim() && !form.name.trim()}
            onClick={async () => { await save([...(project.members || []), form], project.mentors); setForm({ name: '', email: '', role: 'Member' }); setAdding(false); toast.success('Member added'); }}>
            Save
          </button>
        </div>
      )}

      <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-sky-300 mb-4 mt-10">Mentors / guides</div>
      <div className="space-y-2.5">
        {(project.mentors || []).length === 0 && <div className="text-[12px] text-white/35 italic">No mentor assigned yet.</div>}
        {(project.mentors || []).map((m, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-sky-400/20 bg-sky-400/[0.04] p-3.5">
            <UserRound className="w-5 h-5 text-sky-300 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] text-white/90 font-medium truncate">{m.name || m.email}</div>
              <div className="text-[11px] text-white/40 truncate">{m.email}</div>
            </div>
            {canEdit && (
              <button onClick={() => save(project.members, project.mentors.filter((_, j) => j !== i))} className="text-white/25 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        {canEdit && (
          <MentorAdd onAdd={(m) => save(project.members, [...(project.mentors || []), m])} />
        )}
      </div>
    </div>
  );
}

const MentorAdd = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  if (!open) return <button className={btnGhost} onClick={() => setOpen(true)}><Plus className="w-3.5 h-3.5" /> Add mentor</button>;
  return (
    <div className="flex flex-wrap gap-2 items-center rounded-xl border border-white/12 p-3 bg-slate-900/60">
      <input className={`${inputCls} flex-1 min-w-[120px]`} placeholder="Mentor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input className={`${inputCls} flex-1 min-w-[170px]`} placeholder="mentor@org.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <button className={btnGhost} disabled={!form.email.trim()} onClick={() => { onAdd(form); setForm({ name: '', email: '' }); setOpen(false); }}>Save</button>
    </div>
  );
};
